import React, { useRef, useEffect } from "react";
import { NumericFormat } from "react-number-format";
import { createNumberMask } from "text-mask-addons";
import { Formik, Form, Field } from "formik";
import MaskedInput from "react-text-mask/dist/reactTextMask";
import { useHistory } from "react-router";
import { InfoIcon } from "components";
import { object, string } from "yup";
import { shallowEqual, useSelector } from "react-redux";
import { getToken, getTransactionType } from "globals/auth";
import { Row, Col, Container } from "reactstrap";
import { TRNMask } from "components/ClaimsProcess/AccidentDetailsThirdParty/constants";
import { formatMoney } from "components/RenewalProcess/AdditionalBenefits/functions";
import { TRANSACTION_TYPES } from "pages/Payments/constants";
import { QUICK_PAY } from "pages/RenewPolicy/constants";
import FloatingLabelInput from "components/NewFloatingLabel/FloatingLabelInput";
import FloatingLabelSelect from "components/NewFloatingLabel/FloatingLabelSelect";

// Static image paths from public folder
const LockIcon = "/assets/img/payment_form_icons/lock.svg";
const JMDIcon = "/assets/img/payment_form_icons/JMD.svg";
const IdCardIcon = "/assets/img/payment_form_icons/id-card.svg";
const HashIcon = "/assets/img/payment_form_icons/number_sign.svg";

const numberMaskObject = {
	prefix: "",
	includeThousandsSeparator: false,
	integerLimit: 7,
};

const numberMask = createNumberMask(numberMaskObject);

/**
 * Schema using the Yup library for more readable field object validation
 */
const paymentFormSchema = object().shape({
	nationalId: string()
		.required("Please enter a TRN")
		.matches(/^[\d]{3}-[\d]{3}-[\d]{3}$/, "Please enter a valid TRN"),
	policyNumber: string()
		.required("Please enter a valid policy number. Digits only.")
		.matches(/^\d+$/g, "Only numbers are accepted")
		.min(1, `Policy number must be between one (1) and seven (${numberMaskObject.integerLimit}) digits`)
		.max(
			numberMaskObject.integerLimit,
			`Policy number must be between one (1) and seven (${numberMaskObject.integerLimit}) digits`
		),

	paymentAmount: string()
		.required("Please enter a valid payment amount. Decimal digits are allowed.")
		.matches(/^(\d{1,3}(,\d{3})*|\d+)(\.\d{2})?$/, "Payment amount format is invalid")
		.test("not-zero", "Please enter a valid payment amount", value => {
			if (!value) return false;
			const numericValue = parseFloat(value.replace(/,/g, ''));
			return numericValue > 0;
		}),
	currencyType: string().required("Please select a currency type"),
	paymentPurpose: string().required("Please choose a valid transaction type").min(1),
	paymentPurposeDetailed: string().when("paymentPurpose", {
		is: paymentPurpose => paymentPurpose === "Other",
		then: string().required("Please provide a detailed transaction type"),
	}),
});

/**
 * Handles the payment confirmation process
 * @param {Object} props
 * @param {React.Dispatch<React.SetStateAction<boolean>>} props.confirmPayment
 * @param {Boolean} props.paymentConfirmed
 * @param {React.Dispatch<defaultFormValues>} props.setPaymentDetails
 * @param {Object} props.transaction
 * @param {Function} props.validateTrnPolicy2
 * @param {Function} props.showError
 * @param {React.MutableRefObject<Boolean>} props.persist
 */
function 
PaymentDetails({
	transaction,
	confirmPayment,
	paymentConfirmed,
	setPaymentDetails,
	validateTrnPolicy2,
	showValidationError,
	persist,
	updateStep
}) {
	const history = useHistory();
	const confirmRef = useRef(null);
	const token = useSelector(getToken, shallowEqual);
	const transactionType = useSelector(getTransactionType, shallowEqual);
	/** Initializers for form state */
	const defaultFormValues = {
		...transaction,
		currencyType: "JMD",
		paymentPurposeDetailed: "",
		fromRenewalFlow: transaction.policyNumber === null ? false : true,
		nationalId: transaction.nationalId === null ? "" : transaction.nationalId,
		policyNumber: transaction.policyNumber === null ? "" : transaction.policyNumber,
		paymentAmount: transactionType === QUICK_PAY ? "" : transaction.paymentAmount === null ? "0.00" : transaction.paymentAmount,
		paymentPurpose: transaction.paymentPurpose === null ? "" : transaction.paymentPurpose,
	};

	useEffect(() => {
		if (transaction.policyNumber) setPaymentDetails(defaultFormValues);
		return () => {};
	}, [transaction.policyNumber]);

	/**
	 * Submission handler for form
	 * @param {defaultFormValues} values
	 * @param {Object} props
	 * @param {Function} props.setSubmitting
	 */
	async function handleFormSubmit(values, { setSubmitting }) {
		let validationResult = await validateTrnPolicy2( transactionType, values?.nationalId, values?.policyNumber); 
 
		if (transactionType === QUICK_PAY && (validationResult === 1 || validationResult === 2 || validationResult === 3)) {
			if (validationResult === 1) {
				// failed validation for trn and policy
				showValidationError(
					"This policy is either not active or the TRN and policy number are invalid. Please confirm the details entered or contact our customer support."
				);
			} else {
				// service failed
				showValidationError("Service unavailable");
			}
		} else {
			updateStep(2, "fwd"); 
		 	if (paymentConfirmed && !values.fromRenewalFlow) {
				confirmPayment(false);
			} else if (!paymentConfirmed) {
				confirmPayment(true);
				setPaymentDetails({ ...values, paymentAmount: values.paymentAmount.replaceAll(",", "") });
			} else {
				persist.current = true;
				history.goBack();
			}
		} 
		setSubmitting(false);
	}

	const transactionTypeOptions = [...TRANSACTION_TYPES.map((val) => {
		return { value: val, label: val }
	})];
 

	/** A helper function to check whether to disable specific fields */
	function propsDisabled(name) {
		return transaction[name] !== null && defaultFormValues[name] !== "";
	}

	/** Checks whether the submit button should be disabled */
	function shouldDisableSubmit(values, errors) {
		let hasEmptyValues = () => {
			if (
				(values.nationalId === "" ||
				values.policyNumber === "" ||
				values.paymentAmount === "0.00" ||
				values.paymentPurpose === "") || Object.entries(errors).length > 0
			)
				return true;
			return false;
		};

		if (paymentConfirmed) return undefined;

		if (!hasEmptyValues()) return false;
		// else if (Object.entries(errors).length === 0 && !hasEmptyValues()) return false;

		return true;
	}

	// Check if fields are disabled/locked (readonly)
	const isTrnLocked = propsDisabled("nationalId") || paymentConfirmed;
	const isPolicyNumberLocked = propsDisabled("policyNumber") || paymentConfirmed;
	const isPaymentAmountLocked = paymentConfirmed || propsDisabled("paymentPurpose");
	const isPaymentPurposeLocked = paymentConfirmed || propsDisabled("paymentPurpose");

	return (
		<Formik
			initialValues={defaultFormValues}
			validationSchema={paymentFormSchema}
			onSubmit={handleFormSubmit}
			initialTouched={{ currencyType: true }}
		>
			{({ setFieldValue, values, isSubmitting, submitCount, errors, handleBlur, touched, setFieldTouched }) => (
				<div className="payment-details-card">
					<Form className="form" style={{ background: "transparent", padding: "0", borderRadius: "0" }}>
						<Container style={{ padding: 0 }}>
							<Row className="justify-content-between">
								<Col md={6} lg={transactionType === "quick-pay" ? 6 : 6}
								style={{
									marginTop: transactionType === QUICK_PAY ? "0" : "20px"
								}}
								>
									<FloatingLabelInput
										as="input"
										id="nationalId"
										mask={TRNMask}
										inputType="mask"
										className="outlined-input"
										label={
											<div style={{ whiteSpace: "nowrap" }}>
												<span className="field-title">Policy Holder's TRN</span>
												<span className="required"> * </span>
												<InfoIcon label={`TRN (Not more than 9 digits)`} />
											</div>
										}
										value={values.nationalId}
										onChange={e => setFieldValue("nationalId", e.target.value)}
										onBlur={() => setFieldTouched("nationalId", true)}
										type="text"
										placeholder="111-222-333"
										disabled={isTrnLocked}
										error={touched.nationalId && errors.nationalId}
										touched={touched.nationalId}
										leftIcon={<img src={IdCardIcon} alt="" style={{ width: 20, height: 20 }} />}
										rightIcon={isTrnLocked ? <img src={LockIcon} alt="" style={{ width: 20, height: 20 }} /> : null}
										required
									/>
								</Col>
								<Col md={6} lg={transactionType === "quick-pay" ? 6 : 6}
								style={{
									marginTop: transactionType === QUICK_PAY ? "0" : "20px"
								}}
								>
									<FloatingLabelInput
										as="input"
										id="policyNumber"
										mask={numberMask}
										inputType="mask"
										className="outlined-input"
										label={
											<div style={{ whiteSpace: "nowrap" }}>
												<span className="field-title">Policy Number</span>
												<span className="required"> * </span>
											</div>
										}
										value={values.policyNumber}
										onChange={e => setFieldValue("policyNumber", e.target.value)}
										onBlur={() => setFieldTouched("policyNumber", true)}
										type="text"
										placeholder="0000000"
										disabled={isPolicyNumberLocked}
										error={errors.policyNumber}
										touched={touched.policyNumber}
										leftIcon={<img src={HashIcon} alt="" style={{ width: 22, height: 22 }} />}
										rightIcon={isPolicyNumberLocked ? <img src={LockIcon} alt="" style={{ width: 20, height: 20 }} /> : null}
										required
									/>
								</Col>
							</Row>
							<Row className="justify-content-between"> 
								<Col md={6} lg={transactionType === "quick-pay" ? 6 : 6}
								style={{
									marginTop: transactionType === QUICK_PAY ? "0" : "20px"
								}}
								>
									<FloatingLabelInput
										as="input"
										id="paymentAmount"
										inputType="currency-input-dropdown"
										className="outlined-input"
										label={
											<div style={{ whiteSpace: "nowrap" }}>
												<span className="field-title">Payment Amount</span>
												<span className="required"> * </span>
											</div>
										}
										value={values.paymentAmount}
										onChange={e => setFieldValue("paymentAmount", e.target.value)}
										onBlur={() => setFieldTouched("paymentAmount", true)}
										type="text"
										placeholder="0.00"
										pattern={undefined}
										disabled={isPaymentAmountLocked}
										disableDropDown={true}
										dropDownOnChange={(currency) => setFieldValue("currencyType", currency)}
										disableSelect={true}
										error={errors.paymentAmount}
										touched={touched.paymentAmount}
										rightIcon={isPaymentAmountLocked ? <img src={LockIcon} alt="" style={{ width: 20, height: 20 }} /> : null}
									/>
								</Col>

								<Col md={6} lg={transactionType === "quick-pay" ? 6 : 6}
								style={{
									marginTop: transactionType === QUICK_PAY ? "0" : "20px"
								}}
								>
									<FloatingLabelSelect
										as="select"
										id="paymentPurpose"
										label={
											<div style={{ whiteSpace: "nowrap" }}>
												<span className="field-title">Transaction Type</span>
												<span className="required"> * </span>
											</div>
										}

										options={transactionTypeOptions}
										className="outlined-input"
										value={[ { value: values.paymentPurpose, label: values.paymentPurpose } ]}
										onChange={val => setFieldValue("paymentPurpose", val.value)}
										placeholder="Renewal"
										isDisabled={isPaymentPurposeLocked}
										rightIcon={isPaymentPurposeLocked ? <img src={LockIcon} alt="" style={{ width: 20, height: 20 }} /> : null}
										error={errors.paymentPurpose}
										required
									/>

									{touched.paymentPurpose && errors.paymentPurpose && (
										<div className="text-danger pt-2 pl-2">{errors.paymentPurpose}</div>
									)}
								</Col>
								{values.paymentPurpose === "Other" && (
									<Col
										style={{
											marginTop: transactionType === QUICK_PAY ? "0" : "20px"
										}}
									>
										<FloatingLabelInput
											as="input"
											id="paymentPurposeDetailed"
											name="paymentPurposeDetailed"
											className="outlined-input"
											type="text"
											disabled={paymentConfirmed}
											label={
												<div style={{ whiteSpace: "nowrap" }}>
													<span className="field-title">Transaction Details</span>
													<span className="required"> * </span>
												</div>
											}
											onChange={e => setFieldValue("paymentPurposeDetailed", formatMoney(e.target.value, 2, ".", ",", false, false) )}
											onBlur={() => setFieldTouched("paymentPurposeDetailed", true)}
											touched={touched.paymentPurposeDetailed}
											error={errors.paymentPurposeDetailed}
										/>
									</Col>
								)}
							</Row>
						</Container>
						{(transactionType === QUICK_PAY ) && (
							<div className="payment-button-container container">
								<button
									className={
										`button-v2 primary-theme-btn` +
										`${
											submitCount === 0 &&
											(values.policyNumber === "" || values.paymentAmount === "" || values.paymentPurpose === "")
												? " initialHidden"
												: ""
										}` +
										`${paymentConfirmed ? " alt" : ""}` +
										`${values.fromRenewalFlow ? "" : " end"}`
									}
									type="submit"
									disabled={shouldDisableSubmit(values, errors) || isSubmitting || undefined}
									ref={confirmRef}
									tabIndex={0}
									style={{ marginTop: 15 }}
								>
									{values.fromRenewalFlow ? "Back" : paymentConfirmed ? "Change" : "Confirm"}
								</button>
							</div>
						)}
					</Form>
				</div>
			)}
		</Formik>
	);
}

export default PaymentDetails;
